import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "@/components/ui/sonner";
import { Trash2, Pencil, Plus } from "lucide-react";
import { ImageUploader } from "@/components/admin/ImageUploader";

const EMPTY = {
  title_fr: "", title_pt: "", slug: "", excerpt_fr: "", excerpt_pt: "",
  content_fr: "", content_pt: "", cover_image: "", category: "Geral", published: true,
};

export const BlogManager = () => {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const { data: posts = [] } = useQuery({ queryKey: ["admin-blog"], queryFn: async () => (await api.get("/admin/blog")).data });

  const save = useMutation({
    mutationFn: async () => (editing ? api.put(`/admin/blog/${editing.id}`, form) : api.post("/admin/blog", form)),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-blog"] });
      qc.invalidateQueries({ queryKey: ["blog"] });
      toast("Artigo salvo!");
      setOpen(false);
      setForm(EMPTY);
      setEditing(null);
    },
    onError: (e) => toast(formatApiError(e.response?.data?.detail)),
  });

  const remove = useMutation({
    mutationFn: async (id) => api.delete(`/admin/blog/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-blog"] });
      qc.invalidateQueries({ queryKey: ["blog"] });
      toast("Artigo removido.");
    },
  });

  const openEdit = (post) => { setEditing(post); setForm(post); setOpen(true); };
  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };

  return (
    <div className="mt-6" data-testid="blog-manager">
      <div className="flex justify-end mb-4">
        <Button onClick={openNew} data-testid="blog-new-button"><Plus className="w-4 h-4 mr-1" /> Novo artigo</Button>
      </div>
      <Table>
        <TableHeader>
          <TableRow><TableHead>Título (PT)</TableHead><TableHead>Categoria</TableHead><TableHead>Publicado</TableHead><TableHead /></TableRow>
        </TableHeader>
        <TableBody>
          {posts.map((p) => (
            <TableRow key={p.id} data-testid={`blog-row-${p.id}`}>
              <TableCell>{p.title_pt}</TableCell>
              <TableCell>{p.category}</TableCell>
              <TableCell>{p.published ? "Sim" : "Não"}</TableCell>
              <TableCell className="flex gap-2 justify-end">
                <Button size="icon" variant="ghost" onClick={() => openEdit(p)} data-testid={`blog-edit-${p.id}`}><Pencil className="w-4 h-4" /></Button>
                <Button size="icon" variant="ghost" onClick={() => remove.mutate(p.id)} data-testid={`blog-delete-${p.id}`}><Trash2 className="w-4 h-4" /></Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="blog-form-dialog">
          <DialogHeader><DialogTitle>{editing ? "Editar artigo" : "Novo artigo"}</DialogTitle><DialogDescription>Preencha os campos em português e francês.</DialogDescription></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Título (PT)</Label><Input data-testid="blog-input-title_pt" value={form.title_pt} onChange={(e) => setForm({ ...form, title_pt: e.target.value })} /></div>
            <div><Label>Título (FR)</Label><Input value={form.title_fr} onChange={(e) => setForm({ ...form, title_fr: e.target.value })} /></div>
            <div className="col-span-2"><Label>Slug</Label><Input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} /></div>
            <div><Label>Resumo (PT)</Label><Textarea value={form.excerpt_pt} onChange={(e) => setForm({ ...form, excerpt_pt: e.target.value })} /></div>
            <div><Label>Resumo (FR)</Label><Textarea value={form.excerpt_fr} onChange={(e) => setForm({ ...form, excerpt_fr: e.target.value })} /></div>
            <div className="col-span-2">
              <p className="text-xs text-black/50 mb-1">
                Dica: use <code>#</code> para título grande, <code>##</code> para subtítulo, <code>###</code> para título pequeno dentro do conteúdo.
              </p>
            </div>
            <div><Label>Conteúdo (PT)</Label><Textarea rows={5} value={form.content_pt} onChange={(e) => setForm({ ...form, content_pt: e.target.value })} /></div>
            <div><Label>Conteúdo (FR)</Label><Textarea rows={5} value={form.content_fr} onChange={(e) => setForm({ ...form, content_fr: e.target.value })} /></div>
            <div><Label>Categoria</Label><Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} /></div>
            <div className="col-span-2">
              <Label>Imagem de capa</Label>
              <ImageUploader value={form.cover_image} onChange={(url) => setForm({ ...form, cover_image: url })} testId="blog-cover" />
            </div>
          </div>
          <Button onClick={() => save.mutate()} disabled={save.isPending} data-testid="blog-save-button" className="mt-4">Salvar</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
};
